import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AuthgardAdminAction, AuthgardMfaStatus, UserType } from "src/common/enums";
import { humanizeAdminErrorCode, isExpiryPast, normalizeMfaStatus, parseExpiryMs } from "src/common/util";
import { executeAuthgardAdminOperation } from "src/components/auth/authgard-admin-provider";
import { getMfaStatuses } from "src/components/auth/mfa-status-provider";
import { AuthgardResultStatus } from "src/models/authgard-item-result";
import { AuthgardCredentialType } from "src/models/bulk-execute-authgard-admin-operation";
import { logger } from "src/logger";

// The Authgard credential type this modal manages. Alias rather than a fresh union: the provider
// layer already owns the set, and re-spelling it here let the two drift.
export type MfaModalProvider = AuthgardCredentialType;

// Invite fields (expiry + registration code) only exist while an enrollment is active
// (INITIATED / REINITIATED / REREGISTER). `invite: null` is the terminal case (NO_RECORD /
// ONBOARDED), which makes "an expiry or code on a terminal status" unrepresentable. The inner
// fields stay individually nullable to mirror the backend result (a status can arrive before its
// code is surfaced, and the code is masked until an explicit reveal).
interface Invite {
    expiry: Date | null;
    regCode: string | null;
}

interface Enrollment {
    status: AuthgardMfaStatus;
    invite: Invite | null;
}

// Fired whenever the server confirms this principal's enrollment state — a successful admin
// operation, or a successful Refresh read. The parent uses it to keep the card's seed values
// (initialMfaStatus / initialMfaExpiry) in sync, so the card reflects the write and reopening the
// modal doesn't revert to the pre-write state. `expiryIso` is the invite expiry (ISO-8601), or
// null when the confirmed state carries no pending invite.
export type ManageScan2AuthStatusChange =
    (principalArn: string, status: AuthgardMfaStatus, expiryIso: string | null) => void;

export interface UseManageScan2AuthArgs {
    open: boolean;
    principalArn: string;
    // Scope (site) and userType are required by bulkExecuteAuthgardAdminOperation's target input.
    scope: string;
    userType: string;
    mfaProvider: MfaModalProvider;
    initialStatus: AuthgardMfaStatus;
    // Raw ISO-8601 invite expiry from bulkGetMfaStatus (or null when there's no pending invite).
    // Seeds the invite's expiry directly — the hook no longer fabricates one.
    initialExpiry?: string | null;
    // Notifies the parent of every server-confirmed enrollment state (see
    // ManageScan2AuthStatusChange). Optional — the hook works standalone without a listener.
    onStatusChange?: ManageScan2AuthStatusChange;
}

export interface UseManageScan2AuthResult {
    // state (currentStatus / expiryDate / regCode are derived projections of the internal
    // Enrollment — the render reads them as before, but illegal combinations can't be constructed)
    currentStatus: AuthgardMfaStatus;
    expiryDate: Date | null;
    regCode: string | null;
    revealed: boolean;
    errorMessage: string | null;
    submitting: boolean;
    // True while handleRefresh is re-fetching this principal's live status.
    refreshing: boolean;
    // derived (isExpired is intentionally NOT exposed — it's purely presentational and the render
    // derives it from expiryDate, so keeping it here would be a redundant second source of truth)
    hasActiveEnrollment: boolean;
    // actions (the confirm/keep toggle lives in the ConfirmableAction UI component, not here)
    handleGenerateCode: () => void;
    handleRevealCode: () => void;
    cancelEnrollment: () => Promise<boolean>;
    revoke: () => Promise<boolean>;
    handleRefresh: () => Promise<void>;
    resetState: () => void;
}

/**
 * Owns all state + admin-operation orchestration for the single-principal Authgard MFA
 * management modal (Scan2Auth / FIDO2). The modal itself becomes presentational: it renders this
 * hook's returned state and wires the returned actions to buttons. Every admin operation is
 * authorized server-side by PCS/Authgard — this hook never gates authorization.
 */
export const useManageScan2Auth = (args: UseManageScan2AuthArgs): UseManageScan2AuthResult => {
    const { open, principalArn, scope, userType, mfaProvider, initialStatus, initialExpiry, onStatusChange } = args;
    // Parse the ISO expiry to a stable primitive (see parseExpiryMs) so the open effect below keys
    // off the millis, not a fresh Date instance each render.
    const initialExpiryMs = parseExpiryMs(initialExpiry);

    const { t } = useTranslation();

    // Domain state: the MFA status plus (only when active) its invite. Server-authoritative.
    const [enrollment, setEnrollment] = useState<Enrollment>({
        status: AuthgardMfaStatus.NO_RECORD,
        invite: null,
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Guards against concurrent/double submits of an admin operation. The ref is the source of
    // truth for the guard (synchronous); `submitting` drives the disabled state on the buttons.
    const [submitting, setSubmitting] = useState<boolean>(false);
    const submittingRef = useRef<boolean>(false);
    // Drives the disabled state on the Refresh button while a live-status re-fetch is in flight.
    const [refreshing, setRefreshing] = useState<boolean>(false);
    // Monotonic modal-session/request epoch. Each refresh tags itself with a fresh id, and
    // resetState bumps it, so any async result — a refresh OR an admin operation — that resolves
    // after the modal was reset/reopened (or after a newer refresh superseded it) is discarded
    // instead of clobbering the re-seeded state.
    const refreshIdRef = useRef<number>(0);

    // Flat projections of the enrollment for the presentational layer.
    const currentStatus = enrollment.status;
    const expiryDate = enrollment.invite?.expiry ?? null;
    const regCode = enrollment.invite?.regCode ?? null;

    const isExpired = isExpiryPast(expiryDate?.getTime() ?? null);

    // "Revealed" is not separate state: we hold a code exactly when it should be shown. The read
    // (bulkGetMfaStatus) never returns a code, so `regCode` is null on open — the row renders masked
    // with a Reveal button. A freshly generated code (INITIATE/REREGISTER) or an explicit
    // FETCH_REGCODE populates `regCode`, at which point it's shown. There is no state where we hold
    // a code but keep it masked, so a present code == revealed. (Add real state only if a
    // "hide again" / auto-re-mask affordance is ever introduced.)
    const revealed = regCode !== null;

    const applyInitialStatus = (status: AuthgardMfaStatus, expiryMs: number | null) => {
        // The read surfaces status + the real invite expiry but no code (mirrors bulkGetMfaStatus),
        // so an active enrollment opens with its actual expiry and regCode: null — masked, with a
        // Reveal button that fetches the code via FETCH_REGCODE. NO_RECORD / ONBOARDED are terminal
        // (no invite); expired-ness is derived from the expiry date, never fabricated or stored.
        if (status === AuthgardMfaStatus.NO_RECORD || status === AuthgardMfaStatus.ONBOARDED) {
            setEnrollment({ status, invite: null });
        } else {
            setEnrollment({ status, invite: { expiry: expiryMs !== null ? new Date(expiryMs) : null, regCode: null } });
        }
    };

    const resetState = () => {
        // Invalidate any in-flight refresh so its result can't clobber the state we re-seed on reopen.
        refreshIdRef.current += 1;
        applyInitialStatus(initialStatus, initialExpiryMs);
        setErrorMessage(null);
        setSubmitting(false);
        submittingRef.current = false;
        setRefreshing(false);
    };

    // Initialize modal state from the principal's status + real invite expiry when opened.
    useEffect(() => {
        if (open) {
            applyInitialStatus(initialStatus, initialExpiryMs);
        }
    }, [open, initialStatus, initialExpiryMs]);

    // Publishes a server-confirmed enrollment state to the parent (card write-back). Fired only
    // for states the server itself vouched for — admin-operation results and successful Refresh
    // reads — never for parent-seeded state, so the card converges on server truth. A listener
    // fault is isolated here: it must never be attributed to the operation itself (that would
    // repaint a successful write as a failure — the exact defect this hook fixes).
    const publishStatus = (status: AuthgardMfaStatus, expiry: Date | null) => {
        try {
            onStatusChange?.(principalArn, status, expiry ? expiry.toISOString() : null);
        } catch (e) {
            logger.error('onStatusChange listener threw while publishing MFA status', e as Error);
        }
    };

    // Maps an admin operation to the enrollment status its success implies, for results that omit
    // userStatus. Authgard V2 models userMfaStatus as optional on its response entry and PCS
    // passes an absent one through verbatim, so a success can arrive carrying a code but no
    // status — observed live on Alpha, where it made applyAdminResult a silent no-op (the modal
    // stayed on the generate view after a successful INITIATE, and still offered Cancel after a
    // successful cancel). FETCH_REGCODE implies no status change (it only surfaces the code), so
    // it maps to null. An INITIATE that was really a re-invite may be REINITIATED server-side;
    // INITIATED renders the same in-progress view and the next read corrects the label.
    const statusImpliedByOperation = (operation: AuthgardAdminAction): AuthgardMfaStatus | null => {
        switch (operation) {
            case AuthgardAdminAction.INITIATE:
                return AuthgardMfaStatus.INITIATED;
            case AuthgardAdminAction.REREGISTER:
                return AuthgardMfaStatus.REREGISTER;
            case AuthgardAdminAction.CANCEL_INITIATE:
                return AuthgardMfaStatus.NO_RECORD;
            case AuthgardAdminAction.FETCH_REGCODE:
                return null;
            default: {
                // Exhaustiveness pin: a new AuthgardAdminAction member must be mapped here
                // explicitly — silently treating it as "no status change" would reintroduce the
                // silent no-op this mapping exists to remove.
                const unreachable: never = operation;
                return unreachable;
            }
        }
    };

    // Derives the enrollment a successful admin-op result puts this principal in, from the pre-op
    // enrollment. Pure — runAdminOp decides what to do with it (apply to modal state, publish to
    // the parent), so a result that lands after the modal was reset can still update the card
    // without stomping the re-seeded modal. Returns null only when the result changes nothing.
    const deriveAdminResult = (
        operation: AuthgardAdminAction,
        success: { regCode?: string | null; userStatus?: string | null; mfaExpiry?: string | null },
        prev: Enrollment,
    ): Enrollment | null => {
        // The authoritative new status: the response's own userStatus when present, else the one
        // the operation implies (Authgard may omit userMfaStatus on success — see above). The
        // backend returns the authoritative status/expiry/code, so we mirror rather than guess.
        const status = success.userStatus
            ? normalizeMfaStatus(success.userStatus)
            : statusImpliedByOperation(operation);
        if (status === AuthgardMfaStatus.NO_RECORD || status === AuthgardMfaStatus.ONBOARDED) {
            // Terminal states carry no invite — but only when no code came with them. A terminal
            // status alongside a live code is contradictory (PCS fails closed on statuses it does
            // not publish, so it should be unreachable), and a real code must never be discarded
            // or rendered as "not enrolled" — that contradiction falls through to the code
            // handling below instead.
            if (!success.regCode) {
                return { status, invite: null };
            }
        } else if (status !== null) {
            // A freshly issued code (INITIATE/REREGISTER) is shown immediately simply by being
            // present in the invite — `revealed` derives from regCode != null. The expiry parses
            // through the same NaN-guard as every other expiry site: a malformed mfaExpiry
            // degrades to null (no expiry) instead of an Invalid Date that never reads as expired.
            const expiryMs = parseExpiryMs(success.mfaExpiry);
            return {
                status,
                invite: {
                    expiry: expiryMs !== null ? new Date(expiryMs) : null,
                    regCode: success.regCode ?? null,
                },
            };
        }
        if (success.regCode) {
            // A code with no usable status: FETCH_REGCODE (no status change — surface the rotated
            // code, taking the response's expiry when the rotation refreshed it), or the
            // contradictory terminal-status-with-code case above. Keep the current in-progress
            // status when we hold one; otherwise the server just proved an enrollment exists that
            // local state didn't know about — surface the generic in-progress state and let the
            // next read correct the exact label.
            const inProgress = prev.status !== AuthgardMfaStatus.NO_RECORD
                && prev.status !== AuthgardMfaStatus.ONBOARDED
                && prev.status !== AuthgardMfaStatus.UNKNOWN
                ? prev.status
                : AuthgardMfaStatus.INITIATED;
            const rotatedExpiryMs = parseExpiryMs(success.mfaExpiry);
            return {
                status: inProgress,
                invite: {
                    expiry: rotatedExpiryMs !== null ? new Date(rotatedExpiryMs) : (prev.invite?.expiry ?? null),
                    regCode: success.regCode,
                },
            };
        }
        return null; // a status-less, code-less success: nothing to apply
    };

    // Runs one admin operation against this principal and applies the result. Returns true on
    // success, false on any failure (setting the user-facing error). PCS authorizes the operation
    // server-side and re-validates the claimed userType — the UI never gates authorization.
    const runAdminOp = async (operation: AuthgardAdminAction): Promise<boolean> => {
        if (submittingRef.current) {
            return false; // guard against a double submit while a request is in flight
        }
        // Modal-session epoch at dispatch: resetState (close/reopen) bumps it, so a result that
        // lands afterwards must not stomp the re-seeded modal state or surface a stale error. The
        // card write-back (publishStatus) is keyed by principalArn and reflects a write the server
        // DID execute, so it fires regardless.
        const epoch = refreshIdRef.current;
        submittingRef.current = true;
        setSubmitting(true);
        setErrorMessage(null);
        try {
            const resultByArn = await executeAuthgardAdminOperation({
                scope,
                operation,
                credentialType: mfaProvider,
                // userType comes off the principal record as a string; assert to UserType for the target.
                targets: [{ principalArn, userType: userType as UserType }],
            });
            const result = resultByArn.get(principalArn);
            if (!result || result.status === AuthgardResultStatus.UNKNOWN) {
                // No result for this target (unknown outcome) — surface a generic failure.
                if (epoch === refreshIdRef.current) {
                    setErrorMessage(t('manage-credentials-action-failed'));
                }
                return false;
            }
            if (result.status === AuthgardResultStatus.FAILURE) {
                if (epoch === refreshIdRef.current) {
                    setErrorMessage(humanizeAdminErrorCode(t, result.errorCode));
                }
                return false;
            }
            // `enrollment` is the pre-op state here: operations serialize behind submittingRef,
            // and a close/reopen mid-flight is exactly what the epoch check handles.
            const next = deriveAdminResult(operation, result.value, enrollment);
            if (next) {
                // Publish unconditionally only when the status is server-vouched — the response's
                // own userStatus, or the operation's definitional outcome — since those reflect a
                // write the server executed even if the modal has since moved on. The
                // FETCH_REGCODE path's status is a locally-held value, so a stale session must
                // not publish (and freshness-stamp) it over fresher card state.
                const serverVouched = !!result.value.userStatus || statusImpliedByOperation(operation) !== null;
                if (serverVouched || epoch === refreshIdRef.current) {
                    publishStatus(next.status, next.invite?.expiry ?? null);
                }
                if (epoch === refreshIdRef.current) {
                    setEnrollment(next);
                }
            }
            return true;
        } catch (e) {
            // A transport/GraphQL failure (or a missing result payload) throws out of the provider.
            // The outcome is *unknown* — the write may or may not have executed server-side — so
            // surface a generic retry message and leave local state untouched rather than implying it
            // either succeeded or was a no-op. Log the underlying error so the failure is diagnosable.
            logger.error(`executeAuthgardAdminOperation failed for operation ${operation}`, e as Error);
            if (epoch === refreshIdRef.current) {
                setErrorMessage(t('manage-credentials-action-failed'));
            }
            return false;
        } finally {
            // Only clear the in-flight flags if this op's modal session is still current — a reset
            // already cleared them (and a newer session may have its own operation in flight).
            if (epoch === refreshIdRef.current) {
                submittingRef.current = false;
                setSubmitting(false);
            }
        }
    };

    // Single "get a valid code" handler, on the V2 vocabulary. INITIATE covers both the first
    // invite and a re-invite for an in-progress enrollment (Authgard V2 folded V1's REINITIATE into
    // INITIATE — the server detects a re-invite itself). A revoked user re-enrolls via REREGISTER,
    // which revokes and issues a fresh re-registration code:
    //   NO_RECORD / INITIATED / REINITIATED  -> INITIATE
    //   REREGISTER                           -> REREGISTER
    const handleGenerateCode = () => {
        const revoked = currentStatus === AuthgardMfaStatus.REREGISTER;
        void runAdminOp(revoked ? AuthgardAdminAction.REREGISTER : AuthgardAdminAction.INITIATE);
    };

    // Reveals a valid code via FETCH_REGCODE. Authgard V2 ROTATES the code on fetch: the response
    // carries a fresh code and any previously issued code is invalidated. The modal warns about
    // this next to the Reveal button (manage-credentials-reveal-rotates).
    const handleRevealCode = () => {
        void runAdminOp(AuthgardAdminAction.FETCH_REGCODE);
    };

    // The confirm/keep toggle now lives in the ConfirmableAction UI component; the hook exposes only
    // the actual mutations. Each returns the runAdminOp result (true on success, false on failure).
    const cancelEnrollment = (): Promise<boolean> => runAdminOp(AuthgardAdminAction.CANCEL_INITIATE);

    const revoke = (): Promise<boolean> => runAdminOp(AuthgardAdminAction.REREGISTER);

    // Pulls this principal's current MFA state from bulkGetMfaStatus and re-seeds the modal from it,
    // so the admin sees the live status after the user enrolls (or an invite lapses) without
    // reopening. Shares submittingRef with the mutations so a refresh and an admin op never overlap.
    const handleRefresh = async (): Promise<void> => {
        if (submittingRef.current) {
            return; // a mutation or another refresh is already in flight
        }
        // Tag this refresh; a result that resolves after a reset/reopen or a newer refresh is dropped
        // rather than applied to a modal that has since moved on to a different principal.
        const requestId = ++refreshIdRef.current;
        submittingRef.current = true;
        setRefreshing(true);
        setErrorMessage(null);
        try {
            const result = (await getMfaStatuses([principalArn])).get(principalArn);
            if (requestId !== refreshIdRef.current) {
                return; // superseded by a reset/reopen or a newer refresh — drop this stale result
            }
            if (!result || result.status !== AuthgardResultStatus.SUCCESS) {
                // Couldn't load this principal's status (failure or unknown) — show the "unavailable"
                // state instead of a fabricated one. Deliberately NOT published to the card: a
                // failed read is not a state change, and downgrading the card's last-known status
                // over a transient error helps nobody.
                setEnrollment({ status: AuthgardMfaStatus.UNKNOWN, invite: null });
                return;
            }
            // mfaProvider IS the credential type, so match on it directly.
            const credential = result.value.find(c => c.credentialType === mfaProvider);
            if (!credential) {
                setEnrollment({ status: AuthgardMfaStatus.NO_RECORD, invite: null });
                publishStatus(AuthgardMfaStatus.NO_RECORD, null);
            } else {
                const status = normalizeMfaStatus(credential.mfaStatus);
                const expiryMs = parseExpiryMs(credential.mfaInviteExpiry);
                applyInitialStatus(status, expiryMs);
                // A successful read is server truth — keep the parent card in sync with it too.
                // Terminal statuses carry no invite, so they publish no expiry (mirrors
                // applyInitialStatus, which only builds an invite for in-progress states).
                const terminal = status === AuthgardMfaStatus.NO_RECORD || status === AuthgardMfaStatus.ONBOARDED;
                publishStatus(status, !terminal && expiryMs !== null ? new Date(expiryMs) : null);
            }
        } catch (e) {
            // The whole request failed — surface "unavailable" rather than leaving a stale or guessed
            // status behind, unless this refresh was already superseded. Log so it's diagnosable.
            logger.error('getMfaStatuses failed while refreshing MFA status', e as Error);
            if (requestId === refreshIdRef.current) {
                setEnrollment({ status: AuthgardMfaStatus.UNKNOWN, invite: null });
            }
        } finally {
            // Only clear the in-flight flags if we're still the current refresh; a superseding reset
            // has already cleared them (and may have started a newer operation we must not disturb).
            if (requestId === refreshIdRef.current) {
                submittingRef.current = false;
                setRefreshing(false);
            }
        }
    };

    // An active, non-expired enrollment (has a live invite). The modal now derives the reg-code
    // reveal affordance per state (from each state's own expiry) rather than reading a hook flag.
    const hasActiveEnrollment = enrollment.invite != null && !isExpired;

    return {
        currentStatus,
        expiryDate,
        regCode,
        revealed,
        errorMessage,
        submitting,
        refreshing,
        hasActiveEnrollment,
        handleGenerateCode,
        handleRevealCode,
        cancelEnrollment,
        revoke,
        handleRefresh,
        resetState,
    };
};
